/**
 * Centralized Framer Motion configuration and animation variants
 * Following best practices for performance and consistency
 */

import type { TargetAndTransition, Transition, Variants } from "framer-motion";

type AnimationVariant = {
  initial?: TargetAndTransition;
  animate?: TargetAndTransition;
  hidden?: TargetAndTransition;
  visible?: TargetAndTransition;
};

/**
 * Shared animation variants for consistent animations across the app
 * Using only transform and opacity for GPU acceleration
 */
export const fadeInUp = {
  initial: { opacity: 0, y: 10 },
  animate: { opacity: 1, y: 0 },
  hidden: { opacity: 0, y: 10 },
  visible: { opacity: 1, y: 0 },
} satisfies AnimationVariant;

export const fadeIn = {
  initial: { opacity: 0 },
  animate: { opacity: 1 },
  hidden: { opacity: 0 },
  visible: { opacity: 1 },
} satisfies AnimationVariant;

export const fadeInScale = {
  initial: { opacity: 0, scale: 0.98 },
  animate: { opacity: 1, scale: 1 },
} satisfies AnimationVariant;

export const fadeInLeft = {
  initial: { opacity: 0, x: -15 },
  animate: { opacity: 1, x: 0 },
} satisfies AnimationVariant;

export const fadeInRight = {
  initial: { opacity: 0, x: 15 },
  animate: { opacity: 1, x: 0 },
} satisfies AnimationVariant;

export const defaultTransition: Transition = {
  duration: 0.4,
  ease: "easeOut",
};

export const fastTransition: Transition = {
  duration: 0.25,
  ease: "easeOut",
};

/**
 * Viewport configuration for whileInView animations.
 * margin triggers 50px before the element reaches the viewport edge,
 * preventing late-fire on fast scrolls.
 */
export const defaultViewport = {
  once: true,
  margin: "-50px",
};

export const staggerContainer: Variants = {
  hidden: {},
  visible: {
    transition: {
      staggerChildren: 0.1,
    },
  },
};

export const staggerItem = {
  hidden: { opacity: 0, y: 10 },
  visible: { opacity: 1, y: 0 },
} satisfies AnimationVariant;
